import { useContext } from 'react'
import { useSelector } from 'react-redux'
import { Helmet } from 'react-helmet-async'
import PropTypes from 'prop-types'

import { DateTime } from 'luxon'

import { faGlobe } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import { sortArrayBy } from '@domain-group/javascript-utils'

import PageContext from '@contexts/pageContext'

import useCurrentUser from '@hooks/useCurrentUser'
import useUser, { generateUrls } from '@hooks/useUser'
import useUsers from '@hooks/useUsers'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'
import ScopedConditionalContent from '@components/ScopedConditionalContent'

import UserListItem from '../UserListItem'
import UsersTools from '../UsersTools'

const buildPageFilterFields = (roles, currentUserPayload) => {
  const { isAdmin, isBrandUser } = currentUserPayload

  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Name, Email, Username or ID',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'api_key_token',
      label: 'Filter by API Key',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'brands',
      },
      hidden: !isAdmin,
      key: 'brand_id',
    },
    {
      callbacks: {
        buildSearchRequestOptions: state => ({ brand_id: state.brand_id }),
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'agencies',
      },
      hidden: !isAdmin || !isBrandUser,
      key: 'agency_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'providers',
      },
      hidden: !isAdmin,
      key: 'provider_id',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      key: 'role_id',
      label: 'Filter by Role',
      values: sortArrayBy(Object.values(roles), 'asc', 'title').map(role => ({
        key: role.id,
        label: role.title,
      })),
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      key: 'created_after',
      label: 'Created After',
    },
  ]
}

const PageContent = (props) => {
  const { pageFilters } = props
  const { created_after } = pageFilters

  const currentUserPayload = useCurrentUser()

  const createdAfterFormatted = created_after ? DateTime.fromISO(created_after, { zone: 'utc' }) : undefined

  const usersPayload = useUsers({
    filters: {
      ...pageFilters,
      created_after: createdAfterFormatted,
    },
    performHttpRequests: true,
    requestOptions: { include_user_association: true },
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredUsers,
    filteredUsersCount,
    hasFilteredUsers,
    loading,
  } = usersPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const userPayload = useUser()
  const {
    callbacks: { addUser },
  } = userPayload

  const { usersIndexUrl } = generateUrls()

  const roles = useSelector(reduxState => reduxState.entities.roles)

  return (
    <>
      <Helmet>
        <title>Users | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button buttonStyle="primaryCreate" onClick={() => addUser()} size="medium">
            Create
          </Button>
        )}
        activeTabBarItemKey="users"
        tabBarItems={[
          {
            href: usersIndexUrl,
            icon: faGlobe,
            key: 'users',
            title: 'Users',
          },
        ]}
        title="Users"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredUsers && <ListBlankState />}

            {!loading && hasFilteredUsers && (
              <DashboardModule title="Users">
                <Box flexDirection="column">
                  <Box flexDirection="column">
                    {filteredUsers.map(user => (
                      <UserListItem key={user.id} user={user} />
                    ))}
                  </Box>

                  <DashboardModule.LoadMoreFooter
                    callbacks={{ loadMore }}
                    canLoadMore={canLoadMore}
                    entityCount={filteredUsersCount}
                    loading={loading}
                  />
                </Box>
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              filterFields={buildPageFilterFields(roles, currentUserPayload)}
              pageFilters={pageFilters}
              title="Filters"
            />

            <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
              <UsersTools pageFilters={pageFilters} />
            </ScopedConditionalContent>
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

PageContent.propTypes = {
  pageFilters: PropTypes.object.isRequired,
}

export default PageContent
